import React, { useState, useEffect } from 'react'
import { Box, Typography, Card, CardContent } from '@mui/material'
import { useGetDonorHealthHistory } from '@/hooks'
import { useDonorContext, useTenantContext } from '@/context'
import { WarningIcon } from '@/icons'
import {
  BloodPressureHistory,
  SingleValueHistory,
  TestResultHistory,
} from '@/components/health-history'
import { ICON_MAP, UNIT_MAP, Y_AXIS_LABEL_MAP } from './utils'
import { HealthHistorySkeleton } from './Skeleton'
import { isHealthHistorySectionVisible } from './sections'

export const DonorHealthHistory = () => {
  const { donor } = useDonorContext()
  const { tenant } = useTenantContext()
  const { getDonorHealthHistory, loadingGetDonorHealthHistory } =
    useGetDonorHealthHistory()

  const [donorHealthHistoryData, setDonorHealthHistoryData] = useState(null)

  const fetchDonorHealthHistory = async () => {
    if (!donor?.id) return
    const response = await getDonorHealthHistory(donor?.id)
    setDonorHealthHistoryData(response?.data || null)
  }

  useEffect(() => {
    fetchDonorHealthHistory()
  }, [donor?.id])

  const historyItems = Array.isArray(donorHealthHistoryData?.history)
    ? donorHealthHistoryData.history
    : []
  const testResultItems = Array.isArray(donorHealthHistoryData?.testResults)
    ? donorHealthHistoryData.testResults
    : []

  const renderHealthHistoryComponent = item => {
    const { title } = item

    switch (title) {
      case 'Blood Pressure':
        return <BloodPressureHistory key={title} data={item} />
      case 'Temperature':
      case 'Weight':
      case 'Pulse':
      case 'Cholesterol':
      case 'Hemoglobin':
        return (
          <SingleValueHistory
            key={title}
            data={item}
            title={title}
            icon={ICON_MAP[title]}
            valueLabel={title}
            unit={UNIT_MAP[title] || ''}
            yAxisLabel={Y_AXIS_LABEL_MAP[title] || ''}
          />
        )
      default:
        return null
    }
  }

  const visibleHistoryItems = historyItems.filter(item =>
    isHealthHistorySectionVisible(item.title, tenant?.configuration_options)
  )

  const hasData = visibleHistoryItems.length > 0 || testResultItems.length > 0

  const disclaimerText =
    tenant?.portal_constants?.HEALTH_HISTORY_DISCLAIMER_MAIN

  return (
    <>
      <Typography variant='h3' fontWeight={700}>
        Health History
      </Typography>
      <Box sx={{ py: 4 }}>
        {disclaimerText && (
          <Box sx={{ mb: 4 }}>
            <Typography variant='h6' fontWeight={600} sx={{ mb: 2 }}>
              Disclaimer
            </Typography>
            <Card
              sx={{
                borderRadius: '8px',
                boxShadow:
                  '0px 4px 15px 0px #4047761A, 0px 8px 4px 0px #DDDFE840',
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '11fr 1fr' },
                    gap: 2,
                    alignItems: 'flex-start',
                  }}
                >
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                    {disclaimerText}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      pt: { xs: 2, md: 0 },
                    }}
                  >
                    <WarningIcon
                      sx={{ fontSize: '50px', color: 'primary.main' }}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}
        {loadingGetDonorHealthHistory && <HealthHistorySkeleton />}
        {!loadingGetDonorHealthHistory && hasData && (
          <Box>
            {visibleHistoryItems.map(item =>
              renderHealthHistoryComponent(item)
            )}
            {testResultItems.map(item => (
              <TestResultHistory
                key={item.title}
                data={item}
                title={item.title}
              />
            ))}
          </Box>
        )}
        {!loadingGetDonorHealthHistory && !hasData && (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              px: 2,
            }}
          >
            <Typography
              variant='h6'
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                mb: 1,
              }}
            >
              No Health History Available
            </Typography>
            <Typography
              variant='body2'
              sx={{
                color: 'text.secondary',
                maxWidth: '500px',
                mx: 'auto',
              }}
            >
              Your health history records will appear here once they are
              available.
            </Typography>
          </Box>
        )}
      </Box>
    </>
  )
}

export default DonorHealthHistory
